// Grid rendering utilities

import { mapState } from '../state/map-state.js';
import { viewState } from '../state/view-state.js';

/**
 * @typedef {object} GridBounds
 * @property {number} startCol
 * @property {number} endCol
 * @property {number} startRow
 * @property {number} endRow
 * @property {number} gridLeft
 * @property {number} gridRight
 * @property {number} gridTop
 * @property {number} gridBottom
 */

/** @type {string} */
const DEFAULT_GRID_COLOR = '#333333';

/**
 * Calculate visible grid bounds
 * @param {number} gridRows - Total rows
 * @param {number} gridCols - Total columns
 * @param {number} gridSize - Cell size in pixels
 * @returns {GridBounds|null}
 */
function getVisibleGridBounds(gridRows, gridCols, gridSize) {
  if (!gridRows || !gridCols) return null;

  const startCol = 0;
  const endCol = gridCols;
  const startRow = 0;
  const endRow = gridRows;

  return {
    startCol,
    endCol,
    startRow,
    endRow,
    gridLeft: startCol * gridSize,
    gridRight: endCol * gridSize,
    gridTop: startRow * gridSize,
    gridBottom: endRow * gridSize,
  };
}

/**
 * Draw grid lines
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {object} options - Rendering options
 * @param {number} [options.gridSize] - Grid cell size (defaults to mapState)
 * @param {number} [options.gridRows] - Number of rows (defaults to mapState)
 * @param {number} [options.gridCols] - Number of columns (defaults to mapState)
 * @param {string} [options.gridColor] - Grid line color
 * @param {number} [options.zoom] - Zoom level (defaults to viewState)
 * @param {boolean} [options.showGrid=true] - Whether to show grid
 */
export function drawGrid(ctx, options = {}) {
  const {
    gridSize = mapState.gridSize,
    gridRows = mapState.gridRows,
    gridCols = mapState.gridCols,
    gridColor = DEFAULT_GRID_COLOR,
    zoom = viewState.zoom,
    showGrid = true,
  } = options;

  if (!showGrid) return;

  const bounds = getVisibleGridBounds(gridRows, gridCols, gridSize);
  if (!bounds) return;

  ctx.strokeStyle = gridColor;
  // Keep grid lines at 1px regardless of zoom
  ctx.lineWidth = 1 / zoom;

  // Draw vertical lines (columns)
  for (let col = bounds.startCol; col <= bounds.endCol; col++) {
    const x = col * gridSize;
    ctx.beginPath();
    ctx.moveTo(x, bounds.gridTop);
    ctx.lineTo(x, bounds.gridBottom);
    ctx.stroke();
  }

  // Draw horizontal lines (rows)
  for (let row = bounds.startRow; row <= bounds.endRow; row++) {
    const y = row * gridSize;
    ctx.beginPath();
    ctx.moveTo(bounds.gridLeft, y);
    ctx.lineTo(bounds.gridRight, y);
    ctx.stroke();
  }
}

/**
 * Draw fog of war (darken unexplored cells)
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Array<{col: number, row: number, visibility?: string}>} visibleCells - Visible cells
 * @param {number} gridSize - Grid cell size
 * @param {number} gridRows - Number of rows
 * @param {number} gridCols - Number of columns
 * @param {object} [opts]
 * @param {string} [opts.fogColor='rgba(0, 0, 0, 0.85)'] - Fog color
 */
export function drawFogOfWar(ctx, visibleCells, gridSize, gridRows, gridCols, opts = {}) {
  const { fogColor = 'rgba(0, 0, 0, 0.85)' } = opts;
  // Build sets: all revealed cells (visible + adjacent) and adjacent-only cells
  const revealedSet = new Set(visibleCells.map((c) => `${c.col},${c.row}`));
  // Draw full fog on cells that are neither visible nor adjacent
  ctx.fillStyle = fogColor;
  for (let row = 0; row < gridRows; row++) {
    for (let col = 0; col < gridCols; col++) {
      if (!revealedSet.has(`${col},${row}`)) {
        ctx.fillRect(col * gridSize, row * gridSize, gridSize, gridSize);
      }
    }
  }

  // Draw lighter tint on adjacent cells
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  for (const cell of visibleCells) {
    if (cell.visibility === 'adjacent') {
      ctx.fillRect(cell.col * gridSize, cell.row * gridSize, gridSize, gridSize);
    }
  }
}

/**
 * Draw dim lighting overlay on cells
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Array<{col: number, row: number, illumination: string}>} cells - Cells with illumination data
 * @param {number} gridSize - Grid cell size
 * @param {string} [dimColor='rgba(0, 0, 0, 0.5)'] - Dim lighting color
 */
export function drawDimLighting(ctx, cells, gridSize, dimColor = 'rgba(0, 0, 0, 0.5)') {
  ctx.fillStyle = dimColor;
  for (const cell of cells) {
    if (cell.illumination === 'dim') {
      ctx.fillRect(cell.col * gridSize, cell.row * gridSize, gridSize, gridSize);
    }
  }
}

/**
 * Desaturate cells whose illumination was promoted by darkvision (per SRD
 * 5.2.1: "you can see only in shades of grey"). Backend marks these cells
 * with `perceivedViaDarkvision: true` on the visibility payload.
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<{col: number, row: number, perceivedViaDarkvision?: boolean}>} cells
 * @param {number} gridSize
 */
export function drawDarkvisionGreyscale(ctx, cells, gridSize) {
  const greyscaled = cells.filter((c) => c.perceivedViaDarkvision);
  if (greyscaled.length === 0) return;
  ctx.save();
  // Canvas 2D 'color' blend forces the destination's hue+saturation to match
  // the source. A grey source (saturation 0) yields a greyscale destination
  // while preserving the original luminosity — i.e., monochrome rendering
  // of whatever was already drawn (map, tokens, dim overlay).
  ctx.globalCompositeOperation = 'color';
  ctx.fillStyle = 'hsl(0, 0%, 50%)';
  for (const cell of greyscaled) {
    ctx.fillRect(cell.col * gridSize, cell.row * gridSize, gridSize, gridSize);
  }
  ctx.restore();
}
